/**
 * Thank-you page — shown to customer after order confirmation.
 * URL: /merci/:orderId
 * - Fires Purchase event on mount (Meta Pixel + TikTok + GA4)
 * - Bilingual FR / AR with RTL support
 * - Shows order summary + next steps
 */
import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useLang, useSettings, fmtDzd, assetUrl } from "../lib/providers";
import { useCustomer } from "../lib/customerAuth";
import Header from "../components/Header";
import Footer from "../components/Footer";
import WhatsAppFloater from "../components/WhatsAppFloater";
import { Button } from "../components/ui/button";
import { CheckCircle2, MessageCircle, Home, Copy } from "lucide-react";
import { toast } from "sonner";
import { firePixelEvent } from "../components/TrackingScripts";

export default function ThankYouPage() {
  const { orderId } = useParams();
  const { lang } = useLang();
  const { settings } = useSettings();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = lang === "ar" ? "شكرا على طلبك — DZ TENNIS SHOP" : "Merci pour votre commande — DZ TENNIS SHOP";
  }, [lang]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/orders/${orderId}`);
        if (cancelled) return;
        setOrder(data);
        // TikTok : identifie l'acheteur (téléphone/email hashés SHA-256) avant l'événement Purchase
        try {
          const sha = async (s) => Array.from(new Uint8Array(await crypto.subtle.digest("SHA-256", new TextEncoder().encode(s)))).map((b) => b.toString(16).padStart(2, "0")).join("");
          const digits = (data.customer?.phone || "").replace(/\D/g, "").slice(-9);
          const email = (data.customer?.email || "").trim().toLowerCase();
          if (window.ttq?.identify && (digits.length === 9 || email)) {
            window.ttq.identify({
              ...(digits.length === 9 ? { phone_number: await sha("+213" + digits) } : {}),
              ...(email ? { email: await sha(email) } : {}),
            });
          }
        } catch { /* tracking best-effort */ }
        // Fire Purchase event (dedup with server-side CAPI via event_id = order.id)
        firePixelEvent("Purchase", {
          value: data.total_eur || 0,
          currency: "EUR",
          content_ids: (data.items || []).map((i) => i.product_id),
          content_type: "product",
          num_items: (data.items || []).reduce((a, b) => a + (b.qty || 1), 0),
          order_id: data.id,
        });
      } catch {
        if (!cancelled) setOrder(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [orderId]);

  const copyOrderId = async () => {
    try {
      await navigator.clipboard.writeText(orderId);
      toast.success(lang === "ar" ? "تم النسخ ✅" : "Numéro copié ✅");
    } catch {
      // Clipboard API may be blocked (insecure context, denied permission) — fallback to execCommand
      try {
        const ta = document.createElement("textarea");
        ta.value = orderId;
        ta.style.position = "fixed"; ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        toast.success(lang === "ar" ? "تم النسخ ✅" : "Numéro copié ✅");
      } catch {
        toast.error(lang === "ar" ? "لم يتم النسخ، انسخه يدويا" : "Copie impossible, copiez manuellement");
      }
    }
  };

  const waPhone = (settings.whatsapp_dz || settings.contact_phone_dz || "").replace(/\D/g, "");
  const waText = lang === "ar"
    ? `السلام عليكم، أنا ${order?.customer?.name || ""}. طلبي رقم ${orderId}`
    : `Salam, Bonjour, je suis ${order?.customer?.name || ""}. Ma commande n° ${orderId}`;
  const waUrl = waPhone ? `https://wa.me/${waPhone}?text=${encodeURIComponent(waText)}` : null;

  // Online payment success indicator — set by Chargily/SofizPay/Guiddini via ?paid=1 redirect
  const params = new URLSearchParams(window.location.search);
  const paidViaOnline = params.get("paid") === "1";

  // Retrait magasin : libellés dédiés (sinon "Stop desk" + identifiant technique du moyen de paiement)
  const isPickupOrder = order?.delivery_type === "pickup";
  const payMethod = (settings.payment_methods || []).find((p) => p.id === order?.payment_method);
  const paymentLabel = payMethod
    ? (lang === "ar" ? (payMethod.label_ar || payMethod.label_fr) : (payMethod.label_fr || payMethod.label_ar))
    : order?.payment_method;

  const trackWaClick = () => firePixelEvent("Contact", { source: "thank_you_page", order_id: orderId });

  return (
    <div className="min-h-screen text-white overflow-x-hidden" dir={lang === "ar" ? "rtl" : "ltr"}>
      <Header />

      <main className="pt-12 pb-20 px-4">
        <div className="max-w-3xl mx-auto">
          {/* Success banner */}
          <div className="flex flex-col items-center text-center py-8 border-b border-neon/20 mb-10">
            <div className="w-20 h-20 bg-neon/20 border-2 border-neon rounded-full flex items-center justify-center mb-5 animate-pulse-slow">
              <CheckCircle2 className="w-10 h-10 text-neon" strokeWidth={2.5} />
            </div>
            <h1 className="font-heading font-black uppercase tracking-tight text-4xl sm:text-5xl lg:text-6xl mb-3" data-testid="thankyou-title">
              {lang === "ar" ? "شكرا على طلبك 🎾" : "Merci pour votre commande 🎾"}
            </h1>
            <p className="text-base sm:text-lg text-white/70 max-w-xl">
              {lang === "ar"
                ? "لقد تم استلام طلبك بنجاح. سنتواصل معك في أقرب وقت لتأكيد التفاصيل."
                : "Votre commande a été reçue avec succès. Nous vous contacterons très rapidement pour confirmer les détails."}
            </p>
          </div>

          {loading ? (
            <div className="text-center text-white/50 py-8">
              {lang === "ar" ? "جاري التحميل..." : "Chargement..."}
            </div>
          ) : !order ? (
            <div className="text-center py-8">
              <p className="text-clay mb-4">
                {lang === "ar" ? "لم يتم العثور على الطلب" : "Commande introuvable"}
              </p>
              <Link to="/" className="text-neon underline">
                {lang === "ar" ? "العودة للصفحة الرئيسية" : "Retour à l'accueil"}
              </Link>
            </div>
          ) : (
            <>
              {paidViaOnline && (
                <div
                  className="border-2 border-neon bg-neon/10 px-6 py-5 mb-8 flex items-center gap-4 animate-in fade-in slide-in-from-top-2 duration-500"
                  data-testid="payment-verified-banner"
                >
                  <div className="w-12 h-12 bg-neon/30 rounded-full flex items-center justify-center shrink-0">
                    <CheckCircle2 className="w-7 h-7 text-neon" strokeWidth={3} />
                  </div>
                  <div className="flex-1">
                    <p className="text-neon font-black uppercase tracking-widest text-sm sm:text-base" data-testid="payment-verified-title">
                      {lang === "ar" ? "✅ تم الدفع بنجاح" : "✅ Paiement vérifié"}
                    </p>
                    <p className="text-xs sm:text-sm text-white/80 mt-1">
                      {lang === "ar"
                        ? "تم تأكيد دفعتك بنجاح. سنبدأ بتجهيز طلبك فورا."
                        : "Votre paiement a été confirmé avec succès. Nous préparons votre commande immédiatement."}
                    </p>
                    {order?.payment_transaction_id && (
                      <p className="text-[10px] sm:text-xs text-white/50 mt-1 font-mono">
                        {lang === "ar" ? "رقم المعاملة" : "Transaction"} : {order.payment_transaction_id}
                      </p>
                    )}
                  </div>
                </div>
              )}

              <CreateAccountPrompt order={order} lang={lang} settings={settings} />

              {/* Order summary */}
              <div className="border border-white/10 bg-black/30 p-6 mb-8" data-testid="order-summary">
                <div className="flex items-center justify-between flex-wrap gap-3 mb-4 pb-3 border-b border-white/10">
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-white/50">
                      {lang === "ar" ? "رقم الطلب" : "Numéro de commande"}
                    </p>
                    <p className="font-heading font-black text-lg mt-1 flex items-center gap-2">
                      {order.id}
                      <button onClick={copyOrderId} className="text-white/40 hover:text-neon transition-colors" data-testid="copy-order-id" aria-label="Copier">
                        <Copy className="w-4 h-4" />
                      </button>
                    </p>
                  </div>
                  <div className={lang === "ar" ? "text-start" : "text-end"}>
                    <p className="text-[10px] uppercase tracking-widest text-white/50">
                      {lang === "ar" ? "المجموع" : "Total"}
                    </p>
                    <p className="font-heading font-black text-2xl text-neon mt-1" data-testid="thankyou-total">
                      {fmtDzd(order.total_dzd || 0)}
                    </p>
                  </div>
                </div>

                {/* Items */}
                <div className="space-y-2 mb-4">
                  {(order.items || []).map((it, idx) => (
                    <div key={idx} className="flex items-center justify-between gap-3 text-sm py-2 border-b border-white/5 last:border-0">
                      <span className="flex-1 truncate">{it.name}</span>
                      <span className="text-white/60 text-xs">× {it.qty}</span>
                    </div>
                  ))}
                </div>

                {/* Delivery info */}
                <div className="grid sm:grid-cols-2 gap-3 text-xs text-white/70 pt-3 border-t border-white/10">
                  <div>
                    <strong className="text-white">{lang === "ar" ? "التوصيل" : "Livraison"} :</strong>{" "}
                    {isPickupOrder
                      ? (lang === "ar" ? "استلام في المتجر (عنابة)" : "Retrait en magasin (Annaba)")
                      : <>{order.delivery_carrier?.toUpperCase()} — {order.delivery_type === "home" ? (lang === "ar" ? "إلى المنزل" : "À domicile") : "Stop desk"}</>}
                  </div>
                  <div>
                    <strong className="text-white">{lang === "ar" ? "الدفع" : "Paiement"} :</strong>{" "}
                    {order.payment_method === "cod" ? (lang === "ar" ? "عند الاستلام" : "À la livraison") : paymentLabel}
                  </div>
                </div>
              </div>

              {/* Next steps */}
              <div className="border border-neon/30 bg-neon/5 p-6 mb-8">
                <h3 className="font-heading font-black uppercase tracking-tight text-lg mb-3 text-neon">
                  {lang === "ar" ? "الخطوات التالية" : "Prochaines étapes"}
                </h3>
                <ol className={`space-y-2 text-sm text-white/80 ${lang === "ar" ? "me-4" : "ms-4"} list-decimal`}>
                  <li>{lang === "ar" ? "سنتصل بك عبر واتساب لتأكيد الطلب (خلال ساعات قليلة)" : "Nous vous contactons sur WhatsApp pour confirmer la commande (sous quelques heures)"}</li>
                  {isPickupOrder ? (
                    <li>{lang === "ar" ? "تدفع وتستلم طلبك في المتجر بعنابة" : "Vous payez et récupérez votre commande en magasin à Annaba"}</li>
                  ) : (<>
                    <li>{lang === "ar" ? "نجهز ونرسل طلبك خلال 24-48 ساعة" : "Nous préparons et expédions votre commande sous 24-48h"}</li>
                    <li>{lang === "ar" ? "ستصلك رسالة برقم التتبع" : "Vous recevez un numéro de suivi par message"}</li>
                  </>)}
                </ol>
              </div>

              {/* CTA buttons */}
              <div className="grid sm:grid-cols-2 gap-3">
                {waUrl && (
                  <a
                    href={waUrl}
                    onClick={trackWaClick}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 py-4 text-xs font-black uppercase tracking-widest bg-[#25D366] text-white hover:opacity-90 transition-opacity"
                    data-testid="thankyou-wa-btn"
                  >
                    <MessageCircle className="w-4 h-4" />
                    {lang === "ar" ? "واتساب" : "Contacter WhatsApp"}
                  </a>
                )}
                <Button
                  onClick={() => navigate("/")}
                  className="bg-white/10 hover:bg-white/20 border border-white/10 rounded-none py-4 text-xs font-black uppercase tracking-widest h-auto"
                  data-testid="thankyou-home-btn"
                >
                  <Home className="w-4 h-4 me-2" />
                  {lang === "ar" ? "العودة للمتجر" : "Retour à la boutique"}
                </Button>
              </div>

              {/* Track order */}
              <div className="mt-10 text-center">
                <Link to={`/track/${order.id}`} onClick={() => firePixelEvent("TrackOrder", { order_id: order.id })} className="text-sm text-white/60 hover:text-neon underline" data-testid="thankyou-track-link">
                  {lang === "ar" ? "تتبع طلبك" : "Suivre votre commande"} →
                </Link>
              </div>
            </>
          )}
        </div>
      </main>

      <Footer onAdminClick={() => {}} />
      <WhatsAppFloater />
    </div>
  );
}

function CreateAccountPrompt({ order, lang, settings }) {
  const { user, register, googleSignIn } = useCustomer();
  const [open, setOpen] = useState(true);
  const [pwd, setPwd] = useState("");
  const [busy, setBusy] = useState(false);
  if (settings?.customer_accounts_enabled === false) return null;
  if (user || !open) return null;

  const submit = async () => {
    if (pwd.length < 8) {
      toast.error(lang === "ar" ? "كلمة المرور: 8 على الأقل" : "Mot de passe : 8 caractères minimum");
      return;
    }
    setBusy(true);
    try {
      await register({
        email: order?.customer?.email || `${order?.id}@dzts.local`,
        password: pwd,
        name: order?.customer?.name || "",
        phone: order?.customer?.phone || "",
      });
      toast.success(lang === "ar" ? "تم إنشاء الحساب ✅" : "Compte créé ✅ Vos commandes sont liées.");
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-neon/40 bg-neon/5 p-5 mb-8" data-testid="create-account-prompt">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <p className="font-heading font-black uppercase tracking-tight text-lg text-neon">
            {lang === "ar" ? "🎁 أنشئ حسابك في 10 ثواني" : "🎁 Créez votre compte en 10s"}
          </p>
          <p className="text-xs text-white/70 mt-1">
            {lang === "ar"
              ? "احتفظ بسجل طلباتك وحمّل فواتيرك بسهولة."
              : "Retrouvez vos commandes et téléchargez vos factures."}
          </p>
        </div>
        <button onClick={() => setOpen(false)} className="text-white/40 hover:text-white text-sm">✕</button>
      </div>
      <button
        onClick={googleSignIn}
        className="w-full bg-white text-black hover:opacity-90 font-black uppercase tracking-widest text-xs py-3 mb-2 inline-flex items-center justify-center"
        data-testid="prompt-google"
      >
        <svg className="w-4 h-4 me-2" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.5 12.3c0-.8-.1-1.6-.2-2.3H12v4.5h5.9c-.3 1.4-1 2.6-2.2 3.4v2.8h3.6c2.1-1.9 3.2-4.8 3.2-8.4z"/><path fill="#34A853" d="M12 23c2.9 0 5.4-1 7.2-2.6l-3.6-2.8c-1 .7-2.3 1.1-3.6 1.1-2.7 0-5.1-1.8-5.9-4.3H2.4v2.8C4.2 20.6 7.8 23 12 23z"/><path fill="#FBBC05" d="M6.1 14.4c-.2-.7-.4-1.4-.4-2.1s.1-1.4.4-2.1V7.3H2.4C1.6 8.8 1.2 10.4 1.2 12.3s.4 3.5 1.2 5l3.7-2.9z"/><path fill="#EA4335" d="M12 6.4c1.5 0 2.9.5 3.9 1.5l3.2-3.2C17.4 2.9 14.9 1.9 12 1.9 7.8 1.9 4.2 4.3 2.4 7.7l3.7 2.9c.8-2.5 3.2-4.2 5.9-4.2z"/></svg>
        {lang === "ar" ? "متابعة باستعمال Google" : "Continuer avec Google"}
      </button>
      <p className="text-[10px] uppercase tracking-widest text-white/40 my-2 text-center">{lang === "ar" ? "أو بكلمة مرور" : "ou avec un mot de passe"}</p>
      <div className="flex gap-2 flex-wrap">
        <input
          type="password"
          value={pwd}
          onChange={(e) => setPwd(e.target.value)}
          placeholder={lang === "ar" ? "كلمة مرور (8 على الأقل)" : "Mot de passe (8+ caractères)"}
          className="flex-1 min-w-[180px] bg-black/40 border border-white/10 px-3 py-2.5 text-sm focus:border-neon outline-none"
          data-testid="prompt-pwd"
        />
        <Button onClick={submit} disabled={busy || pwd.length < 8} className="bg-neon text-black hover:opacity-90 rounded-none font-black uppercase tracking-widest text-xs px-4 py-2.5 h-auto disabled:opacity-40" data-testid="prompt-submit">
          {busy ? "…" : (lang === "ar" ? "إنشاء" : "Créer")}
        </Button>
      </div>
      <p className="text-[10px] text-white/40 mt-3">
        {lang === "ar"
          ? `سيتم استعمال البريد ${order?.customer?.email || "(الذي قدمته في الطلب)"} والربط الآلي بطلباتك السابقة بنفس رقم الهاتف.`
          : `Email : ${order?.customer?.email || "(celui de votre commande)"}. Toutes vos commandes avec le même numéro seront liées automatiquement.`}
      </p>
    </div>
  );
}
